import { useEffect, useState } from 'react'
import { ScrollText, Loader2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { api } from '@/lib/api'
import { PaginationControls } from '@/components/PaginationControls'
import { toast } from 'sonner'

interface Audit {
  id: string
  superadmin_id: string
  action: string
  target_account_id: string | null
  details: string | null
  ip_address: string
  user_agent: string | null
  created_at: string
}

export function AuditLog() {
  const [audits, setAudits] = useState<Audit[]>([])
  const [loading, setLoading] = useState(true)
  const [offset, setOffset] = useState(0)
  const [total, setTotal] = useState(0)
  const LIMIT = 50

  const fetchAudits = async (newOffset = offset) => {
    setLoading(true)
    try {
      const res = await api.get('/admin/audit-log', {
        params: { limit: LIMIT, offset: newOffset },
      })
      setAudits(res.data.audits || [])
      setTotal(res.data.total || 0)
      setOffset(newOffset)
    } catch {
      toast.error('Failed to load audit log')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAudits(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePageChange = (newOffset: number) => {
    fetchAudits(newOffset)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-primary" />
          Audit Log
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Record of all superadmin actions</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium">Action</th>
                  <th className="px-4 py-3 text-left font-medium">Target</th>
                  <th className="px-4 py-3 text-left font-medium">Details</th>
                  <th className="px-4 py-3 text-left font-medium">IP Address</th>
                  <th className="px-4 py-3 text-left font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : audits.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                      No audit records
                    </td>
                  </tr>
                ) : (
                  audits.map((audit) => (
                    <tr key={audit.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{audit.action}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {audit.target_account_id ? audit.target_account_id.slice(0, 8) + '...' : '—'}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground max-w-xs truncate">
                        {typeof audit.details === 'string'
                          ? audit.details
                          : audit.details && Object.keys(audit.details).length > 0
                            ? JSON.stringify(audit.details)
                            : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{audit.ip_address}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {new Date(audit.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        <PaginationControls offset={offset} limit={LIMIT} total={total} onPageChange={handlePageChange} />
      </Card>
    </div>
  )
}
